import {get, map, clone, reduce, isString, sortBy, set} from 'lodash';
import {snakeCase} from 'change-case';

export function extract(spec, {backend} = {}) {
    return {
        info() {
            return get(spec, 'info');
        },
        contact() {
            return get(spec, 'contact') || [];
        },
        servers() {
            return get(spec, 'servers') || [];
        },
        security() {
            return get(spec, 'security') || [];
        },
        schemaVersion() {
            return get(spec, 'openapi');
        },
        jsonSchemaDialect() {
            // 3.1-only top-level field declaring the default JSON Schema
            // dialect used inside Schema Objects. Returns undefined on 3.0
            // specs (and on 3.1 specs that omit it, which is the common case
            // and implies the 2020-12 dialect).
            return get(spec, 'jsonSchemaDialect');
        },
        version(defaultValue) {
            return get(spec, 'info.version') || defaultValue;
        },
        title(defaultValue) {
            return get(spec, 'info.title') || defaultValue;
        },
        schemas(filterExpression) {
            // key === name (no snakeCase). URL segments now match the OpenAPI
            // JSON key verbatim (e.g. "accessorialCosts.unitBreaks" instead of
            // "accessorial_costs_unit_breaks"). Dots and camelCase are both
            // URL-safe and read closer to the domain vocabulary consortium
            // reviewers use.
            let modelData = get(spec, 'components.schemas') || {};
            let result = map(modelData, (model, key) => ({name: key, key: key, spec: model}));
            if (filterExpression) {
                result = result.filter((r) => r.name.toLowerCase().indexOf(filterExpression.toLowerCase()) !== -1);
            }
            return sortBy(result, 'key');
        },
        parameters(filterExpression) {
            let modelData = get(spec, 'components.parameters') || {};
            let result = map(modelData, (model, key) => ({name: model.name, key: key, spec: model}));
            if (filterExpression) {
                result = result.filter((r) => r.name.toLowerCase().indexOf(filterExpression.toLowerCase()) !== -1);
            }
            return sortBy(result, 'key');
        },
        securitySchemes(filterExpression) {
            let modelData = get(spec, 'components.securitySchemes') || {};
            let result = map(modelData, (model, key) => ({name: key, key: key, spec: model}));
            if (filterExpression) {
                result = result.filter((r) => r.name.toLowerCase().indexOf(filterExpression.toLowerCase()) !== -1);
            }
            return sortBy(result, 'key');
        },
        interfaces(filterExpression) {
            let result = map(get(spec, 'paths') || {}, (pathSpec, path) => ({key: snakeCase(path), path, name: path, spec: pathSpec}));
            if (filterExpression) {
                result = result.filter((r) => r.path.toLowerCase().indexOf(filterExpression.toLowerCase()) !== -1);
            }
            return sortBy(result, 'path');
        },
        // OpenAPI 3.1 introduces a top-level `webhooks` map. Each entry is a
        // PathItem (same shape as an entry under `paths`), so we return the
        // same `{key, path, name, spec}` tuple interfaces() uses. key === name
        // because webhook names are plain identifiers (no URL path segments
        // to encode), so no snakeCase transform is applied.
        webhooks(filterExpression) {
            let result = map(get(spec, 'webhooks') || {}, (pathSpec, name) => ({key: name, path: name, name, spec: pathSpec}));
            if (filterExpression) {
                const needle = filterExpression.toLowerCase();
                result = result.filter((r) => r.name.toLowerCase().indexOf(needle) !== -1);
            }
            return sortBy(result, 'name');
        },
        webhook(key) {
            return this.webhooks().find((w) => w.key === key);
        },
        interface(key) {
            return this.interfaces().find((intf) => intf.key === key);
        },
        model(key) {
            return this.schemas().find((model) => model.key === key);
        },
        parameter(key) {
            return this.parameters().find((model) => model.key === key);
        },
        securityScheme(key) {
            return this.securitySchemes().find((model) => model.key === key);
        },
        async lookup(ref, context) {
            if (!isString(ref)) {
                console.warn('unable to resolve ref', ref);
                return {};
            }

            if (ref.indexOf('#') === 0) {
                const parts = ref.substring(2).split('.').join('$').split('/');
                return reduce(
                    parts,
                    (result, part) => {
                        const fieldKey = part.split('$').join('.');
                        return get(result, fieldKey);
                    },
                    spec
                );
            } else {
                return backend.resolveRef(ref, context);
            }
        },
        async lookupMany(refs, context) {
            // Entries can be either {$ref: '...'} references or inline
            // schema objects (typical for allOf composition — e.g.
            // `[{$ref: 'Document'}, {type:'object', properties:{...}}]`).
            // The old fallback `ref.$ref || ref` passed the inline schema
            // back into lookup(), which isn't a string ref and triggered
            // an "unable to resolve ref" warning. Just return inline
            // schemas as-is; only follow actual refs through lookup.
            return Promise.all(
                refs.map((ref) => (ref && typeof ref.$ref === 'string' ? this.lookup(ref.$ref, context) : ref)),
            );
        },
        async transformParameters(parameters = [], context) {
            return Promise.all(
                parameters.map(async (p, idx) => {
                    let fp = clone(p);
                    if (fp.$ref) {
                        fp = await this.lookup(p.$ref, context);
                        if (fp) {
                            fp.$ref = p.$ref;
                            return fp;
                        } else {
                            return null;
                        }
                    }
                    fp.type = get(fp.schema, 'type');
                    fp.key = `${fp.name}${idx}`;
                    fp.tags = [];
                    if (fp.required) {
                        fp.tags.push({text: 'required', color: 'volcano'});
                    }
                    if (fp.deprecated) {
                        fp.tags.push({text: 'deprecated', color: 'red'});
                    }
                    if (fp.allowEmptyValue) {
                        fp.tags.push({text: 'allow-empty', color: 'green'});
                    }
                    return fp;
                })
            );
        },
        addComponent(type, compSpec) {
            spec.components[type] = spec.components[type] || {};
            set(spec.components[type], compSpec.name, compSpec);
            return this;
        },
    };
}
